import React, { Component } from 'react';
import _ from 'lodash';
import { ngClass } from '../../utils';
import ReactAudioPlayer from './components/ReactAudioPlayer';
import PlayBackButton from './components/PlayBackButton';
import PlayBackTime from './components/PlayBackTime';
import Spinner from './components/Spinner';
import './styles.css';

interface AudioPlayerProps {
  src: string;
  autoPlay?: boolean;
  trackId?: string;
  title?: string;
  artist?: string;
  initialCurrentTime?: number;
  onProgress?: (currentTime: number) => void;
  onPlayBackButtonClick?: (isPaused: boolean) => void;
}

interface AudioPlayerState {
  isPaused: boolean;
  currentTime: number;
  duration: number;
  isReady: boolean;
}

class AudioPlayer extends Component<AudioPlayerProps, AudioPlayerState> {
  private audio?: { audioEl: React.RefObject<HTMLAudioElement> } | null;
  static defaultProps = {
    trackId: '',
    title: '',
    artist: '',
    autoPlay: false,
    initialCurrentTime: 0,
    onProgress: () => {},
  };

  constructor(props: AudioPlayerProps) {
    super(props);

    this.state = {
      isPaused: !props.autoPlay,
      currentTime: props.initialCurrentTime || 0,
      duration: 0,
      isReady: false,
    };

    this.handleEmmaMobileMessage = this.handleEmmaMobileMessage.bind(this);
    this.play = this.play.bind(this);
    this.pause = this.pause.bind(this);
    this.stop = this.stop.bind(this);
    this.destroy = this.destroy.bind(this);
    this.onPlay = this.onPlay.bind(this);
    this.onPaused = this.onPaused.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onListen = this.onListen.bind(this);
    this.onLoadedMetadata = this.onLoadedMetadata.bind(this);
    this.onPlayBackButtonClick = this.onPlayBackButtonClick.bind(this);
  }

  componentDidMount() {
    document.addEventListener(
      'reactNativeMessage',
      this.handleEmmaMobileMessage,
    );
  }

  componentWillUnmount() {
    this.destroy();
    document.removeEventListener(
      'reactNativeMessage',
      this.handleEmmaMobileMessage,
    );
  }

  handleEmmaMobileMessage(event: any) {
    try {
      const message = JSON.parse(event.detail);
      if (message.action && message.action.indexOf('@EMMA_MOBILE/') == 0) {
        if (message.action === '@EMMA_MOBILE/AUDIO_ON_PROGRESS') {
          this.onListen(message.payload.progress, message.payload.isPaused);
        }

        if (message.action === '@EMMA_MOBILE/AUDIO_ON_ENDED') {
          this.onEnded();
        }

        if (message.action === '@EMMA_MOBILE/AUDIO_REMOTE_PLAY') {
          this.onPlay();
        }

        if (message.action === '@EMMA_MOBILE/AUDIO_REMOTE_PAUSE') {
          this.onPaused();
        }

        if (message.action === '@EMMA_MOBILE/AUDIO_REMOTE_STOP') {
          this.onEnded();
        }

        if (message.action === '@EMMA_MOBILE/AUDIO_ON_STATE_UPDATED') {
          const isPaused = _.get(message, 'payload.isPaused', true);
          const progress = _.get(message, 'payload.progress', 0);
          this.onUpdatedTrackState(progress, isPaused);
        }
      }
    } catch (err) {
      return false;
    }
  }

  onListen(currentTime: number, isPaused?: boolean) {
    const currentTimeInt = Math.trunc(currentTime);
    if (this.props.onProgress) this.props.onProgress(currentTimeInt);
    this.setState({
      currentTime: currentTimeInt,
      isPaused: _.isNil(isPaused) ? this.state.isPaused : isPaused,
    });
  }

  onLoadedMetadata(e: any) {
    console.log(e);
    if (this.audio && this.audio.audioEl.current) {
      const duration = Math.floor(this.audio.audioEl.current.duration);
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            action: '@EMMA_MOBILE/AUDIO_ON_METADATA_LOADED',
            payload: {
              id: this.props.trackId,
              src: this.props.src,
              duration,
              title: this.props.title,
              artist: this.props.artist,
            },
          }),
        );
      }

      this.audio.audioEl.current.currentTime = this.state.currentTime;
      this.setState({
        duration,
        isReady: true,
      });
    }
  }

  onUpdatedTrackState(currentTime: number, isPaused: boolean) {
    this.setState({
      isPaused,
      currentTime,
    });
  }

  onPlayBackButtonClick() {
    if (this.state.isPaused) {
      this.play();
    } else {
      this.pause();
    }

    if (this.props.onPlayBackButtonClick) {
      this.props.onPlayBackButtonClick(!this.state.isPaused);
    }
  }

  onPlay() {
    this.setState({ isPaused: false });
  }

  onPaused() {
    this.setState({ isPaused: true });
  }

  onEnded() {
    this.setState({ isPaused: true, currentTime: 0 });
  }

  play() {
    if (this.audio && this.audio.audioEl.current) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            action: '@EMMA_MOBILE/PLAY_AUDIO',
          }),
        );
      } else {
        this.audio.audioEl.current.play();
        this.setState({ isPaused: false });
      }
    }
  }

  pause() {
    if (this.audio && this.audio.audioEl.current) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            action: '@EMMA_MOBILE/PAUSE_AUDIO',
          }),
        );
      } else {
        this.audio.audioEl.current.pause();
        this.setState({ isPaused: true });
      }
    }
  }

  stop() {
    if (this.audio && this.audio.audioEl.current) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            action: '@EMMA_MOBILE/STOP_AUDIO',
          }),
        );
      } else {
        this.audio.audioEl.current.pause();
        this.audio.audioEl.current.currentTime = 0;
        this.setState({ currentTime: 0, isPaused: true });
      }
    }
  }

  destroy() {
    if (this.audio && this.audio.audioEl.current) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            action: '@EMMA_MOBILE/DESTROY_AUDIO',
          }),
        );
      }
    }
  }

  render() {
    const { src, autoPlay } = this.props;
    const { isPaused, currentTime, duration, isReady } = this.state;

    return (
      <div className="audio-player-container">
        <div
          className={ngClass(
            {
              'not-ready': !isReady,
            },
            'audio-player',
          )}
        >
          <PlayBackButton
            onClick={this.onPlayBackButtonClick}
            isPaused={isPaused}
          />

          <PlayBackTime currentTime={currentTime} duration={duration} />
          <div className="audio-player__embedded-audio">
            <ReactAudioPlayer
              ref={(ref: any) => {
                this.audio = ref;
              }}
              src={src}
              autoPlay={autoPlay}
              onPlay={this.onPlay}
              onPause={this.onPaused}
              onListen={this.onListen}
              onEnded={this.onEnded}
              listenInterval={500}
              onLoadedMetadata={this.onLoadedMetadata}
            />
          </div>
        </div>
        {!isReady && <Spinner className="spinner" />}
      </div>
    );
  }
}

export default AudioPlayer;
